import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiRequest, ApiClientError } from '../api/client.js';

type SessionState = 'loading' | 'authenticated' | 'guest';
type SessionContextValue = {
  readonly state: SessionState;
  readonly csrfToken: string | null;
  readonly login: (email: string, password: string) => Promise<void>;
  readonly logout: () => Promise<void>;
  readonly reauthenticate: () => void;
};
const SessionContext = createContext<SessionContextValue | null>(null);
const storedCsrf = (): string | null => {
  try { return window.sessionStorage.getItem('dsh.csrf'); } catch { return null; }
};
const rememberCsrf = (value: string | null): void => {
  try {
    if (value === null) window.sessionStorage.removeItem('dsh.csrf');
    else window.sessionStorage.setItem('dsh.csrf', value);
  } catch { /* An in-memory session is still usable when browser storage is disabled. */ }
};

export const SessionProvider = ({ children }: { readonly children: ReactNode }) => {
  const [state, setState] = useState<SessionState>('loading');
  const [csrfToken, setCsrfToken] = useState<string | null>(storedCsrf);
  useEffect(() => {
    let active = true;
    void apiRequest<{ data: { authenticated: true } }>('/api/v1/auth/session')
      .then(() => { if (active) setState('authenticated'); })
      .catch((error: unknown) => {
        if (active) setState(error instanceof ApiClientError && error.status === 401 ? 'guest' : 'guest');
      });
    return () => { active = false; };
  }, []);
  const login = useCallback(async (email: string, password: string) => {
    const response = await apiRequest<{ data: { csrfToken: string } }>('/api/v1/auth/login', { method: 'POST', body: { email, password } });
    if (typeof response.data?.csrfToken !== 'string' || response.data.csrfToken.length === 0) throw new Error('Invalid session response');
    rememberCsrf(response.data.csrfToken);
    setCsrfToken(response.data.csrfToken);
    setState('authenticated');
  }, []);
  const reauthenticate = useCallback(() => {
    rememberCsrf(null);
    setCsrfToken(null);
    setState('guest');
  }, []);
  const logout = useCallback(async () => {
    try { await apiRequest('/api/v1/auth/logout', { method: 'POST', csrfToken }); }
    catch (error) {
      // A 401 means the server session is already unavailable. Never claim logout
      // succeeded on a network failure or invalid CSRF response.
      if (!(error instanceof ApiClientError && error.status === 401)) throw error;
    }
    rememberCsrf(null);
    setCsrfToken(null);
    setState('guest');
  }, [csrfToken]);
  const value = useMemo(() => ({ state, csrfToken, login, logout, reauthenticate }), [state, csrfToken, login, logout, reauthenticate]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const useSession = (): SessionContextValue => {
  const value = useContext(SessionContext);
  if (value === null) throw new Error('useSession must be used inside SessionProvider');
  return value;
};
